import { useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { portalFetch, KyraApiError } from "@/lib/api";
import type { StartExecutionResponse } from "@/lib/types";

type ExecutionKind = "workout" | "diet";

interface StartState {
  /** Id (workout/diet) cujo start está em curso, ou null. */
  startingId: string | null;
  /** Mensagem de erro amigável, ou null. */
  error: string | null;
}

/**
 * Encapsula o fluxo de iniciar uma execução (treino ou dieta):
 *  - POST /student-portal/me/{workouts|diets}/:id/execute → `execution_id`
 *  - 201/200: navega pra tela de execução (`/s/:slug/treinos|dietas/:execId`)
 *  - 409 (execução in_progress): o body traz o `execution_id` existente →
 *    RETOMAMOS (navega pra ele) em vez de mostrar erro
 *  - 401 (sessão morta): redireciona pro login
 *  - resto: mensagem de erro amigável
 *
 * Porta a lógica idêntica dos HomeView/TreinosView legados (Next), trocando
 * `router.push/replace` por `navigate` do react-router e `useTransition` (que
 * não faz sentido na SPA) por estado simples.
 */
export function useStartExecution() {
  const navigate = useNavigate();
  const { slug = "" } = useParams();
  const [state, setState] = useState<StartState>({ startingId: null, error: null });

  const start = useCallback(
    (kind: ExecutionKind, id: string) => {
      setState((prev) => {
        if (prev.startingId) return prev; // já tem um start em curso
        return { startingId: id, error: null };
      });

      const segment = kind === "workout" ? "workouts" : "diets";
      const routeBase = kind === "workout" ? "treinos" : "dietas";
      const errorMsg =
        kind === "workout"
          ? "Não consegui iniciar o treino. Tente novamente."
          : "Não consegui iniciar a refeição. Tente novamente.";

      (async () => {
        try {
          const res = await portalFetch<StartExecutionResponse>(
            `/student-portal/me/${segment}/${id}/execute`,
            { method: "POST", body: {} },
          );
          if (!res?.execution_id) {
            throw new KyraApiError(500, "no_execution_id", "Resposta inválida");
          }
          navigate(`/s/${slug}/${routeBase}/${res.execution_id}`);
        } catch (err) {
          if (err instanceof KyraApiError && err.status === 401) {
            navigate(`/s/${slug}/login`, { replace: true });
            return;
          }
          // 409 = já existe execução in_progress. O Worker manda o execution_id
          // no body — em vez de erro, RETOMAMOS (navega direto pra execução).
          if (err instanceof KyraApiError && err.status === 409) {
            try {
              const data = JSON.parse(err.body) as {
                error?: string;
                execution_id?: string;
              };
              if (data.error === "execution_in_progress" && data.execution_id) {
                navigate(`/s/${slug}/${routeBase}/${data.execution_id}`);
                return;
              }
            } catch {
              // body não é JSON — cai no erro genérico abaixo.
            }
          }
          console.error("[useStartExecution] start failed", err);
          setState({ startingId: null, error: errorMsg });
        }
      })();
    },
    [navigate, slug],
  );

  return {
    startingId: state.startingId,
    startError: state.error,
    startWorkout: (id: string) => start("workout", id),
    startDiet: (id: string) => start("diet", id),
  };
}
